import request from '@/config/axios'
import * as AiEducationApi from '@/api/aiEducation'
import * as UniversityApi from '@/api/aiEducation/university'

export interface BusinessOption {
  id: number
  name: string
  title?: string
  courseId?: number
  mobile?: string
  nickname?: string
}

export const useBusinessOptions = () => {
  const optionLoading = ref(false)
  const schools = ref<AiEducationApi.SchoolVO[]>([])
  const teachingClasses = ref<AiEducationApi.TeachingClassVO[]>([])
  const terms = ref<UniversityApi.TermSimpleVO[]>([])
  const courses = ref<BusinessOption[]>([])
  const chapters = ref<BusinessOption[]>([])
  const knowledgeBases = ref<BusinessOption[]>([])
  const aiModels = ref<BusinessOption[]>([])
  const students = ref<BusinessOption[]>([])

  const normalizeList = (value: any): any[] =>
    value?.list || value?.data?.list || value?.data || value || []
  const loadBaseOptions = async (schoolId?: number) => {
    optionLoading.value = true
    try {
      const silent = { headers: { ignoreError: true } }
      const [schoolResult, classResult, termResult, courseResult, knowledgeResult, modelResult] =
        await Promise.allSettled([
          AiEducationApi.getSchoolOptions().catch(() =>
            request.get({ url: '/ai-education/school/list', ...silent })
          ),
          AiEducationApi.getTeachingClassList({ schoolId }),
          UniversityApi.getTermSimpleList(schoolId).catch(() => []),
          AiEducationApi.getTrainCourseOptions(200).catch(() => []),
          request.get({ url: '/ai/knowledge/simple-list', ...silent }).catch(() => []),
          request.get({ url: '/ai/model/simple-list', params: { type: 1 }, ...silent }).catch(() => [])
        ])
      if (schoolResult.status === 'fulfilled') schools.value = normalizeList(schoolResult.value)
      if (classResult.status === 'fulfilled')
        teachingClasses.value = normalizeList(classResult.value)
      if (termResult.status === 'fulfilled') terms.value = normalizeList(termResult.value)
      let courseList = courseResult.status === 'fulfilled' ? normalizeList(courseResult.value) : []
      // 禁止回退全站 /train/course：会混入奇门遁甲/编程课等无关项；空则提示先发布课程包
      courses.value = courseList.map((c: any) => ({
        ...c,
        id: c.id,
        name: c.name || c.title || `课程 #${c.id}`,
        title: c.title || c.name
      }))
      if (knowledgeResult.status === 'fulfilled')
        knowledgeBases.value = normalizeList(knowledgeResult.value)
      if (modelResult.status === 'fulfilled') aiModels.value = normalizeList(modelResult.value)
    } finally {
      optionLoading.value = false
    }
  }

  const loadTeachingClasses = async (schoolId?: number) => {
    teachingClasses.value = schoolId ? await AiEducationApi.getTeachingClassList({ schoolId }) : []
  }
  const loadTerms = async (schoolId?: number) => {
    terms.value = schoolId ? await UniversityApi.getTermSimpleList(schoolId) : []
  }
  const loadChapters = async (courseId?: number) => {
    if (!courseId) {
      chapters.value = []
      return
    }
    const tree = normalizeList(
      await request.get({ url: '/train/chapter/tree', params: { courseId } })
    )
    const flatten = (nodes: any[], prefix = ''): BusinessOption[] =>
      nodes.flatMap((node) => {
        const name = `${prefix}${node.name || node.title}`
        return [{ ...node, name }, ...flatten(node.children || [], `${prefix}　`)]
      })
    chapters.value = flatten(tree)
  }
  const searchStudents = async (keyword = '') => {
    const kw = (keyword || '').trim()
    const isMobileLike = /^\d{3,}$/.test(kw)
    const result = await request.get({
      url: '/member/user/page',
      params: {
        pageNo: 1,
        pageSize: 30,
        nickname: !isMobileLike && kw ? kw : undefined,
        mobile: isMobileLike ? kw : undefined
      }
    })
    students.value = normalizeList(result)
  }

  const labelOf = (list: BusinessOption[], id?: number) => {
    const item = list.find((option) => option.id === id)
    return item?.name || item?.title || (id ? `#${id}` : '-')
  }
  const schoolName = (id?: number) => labelOf(schools.value as BusinessOption[], id)
  const className = (id?: number) => labelOf(teachingClasses.value as BusinessOption[], id)
  const termName = (id?: number) => labelOf(terms.value as BusinessOption[], id)
  const courseName = (id?: number) => labelOf(courses.value, id)
  const chapterName = (id?: number) => labelOf(chapters.value, id)
  const knowledgeName = (id?: number) => labelOf(knowledgeBases.value, id)
  const modelName = (id?: number) => labelOf(aiModels.value, id)
  const studentName = (id?: number) => {
    const item = students.value.find((student) => student.id === id)
    return item?.nickname || item?.name || item?.mobile || (id ? `用户 #${id}` : '-')
  }

  return {
    optionLoading,
    schools,
    teachingClasses,
    terms,
    courses,
    chapters,
    knowledgeBases,
    aiModels,
    students,
    loadBaseOptions,
    loadTeachingClasses,
    loadTerms,
    loadChapters,
    searchStudents,
    schoolName,
    className,
    termName,
    courseName,
    chapterName,
    knowledgeName,
    modelName,
    studentName
  }
}
